// ProductFamily — the three remembr editions shown TOGETHER for the
// first time on the unified main landing. Lavender (dementia) flanked by
// pink (baby girl) + blue (baby boy). One shared frame. One forget-me-not.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
};

const EDITIONS = [
    {
        img: "/product-mockup-kids-pink.png",
        captionKey: "kids_pink_caption",
        ring: "border-[#F4C7CC]/60",
        glow: "from-[#FBF1F2]",
        ratio: "aspect-[3/4]",
        scale: "lg:scale-95",
        testid: "product-family-pink",
    },
    {
        img: "/product-mockup-1.png",
        captionKey: "elder_caption",
        ring: "border-[#8C9ECB]/60",
        glow: "from-[#F4F6FB]",
        ratio: "aspect-[3/4]",
        scale: "lg:scale-105 lg:-mt-6",
        testid: "product-family-lavender",
    },
    {
        img: "/product-mockup-kids-blue.png",
        captionKey: "kids_blue_caption",
        ring: "border-[#B9CCE6]/60",
        glow: "from-[#EAF2F9]",
        ratio: "aspect-[3/4]",
        scale: "lg:scale-95",
        testid: "product-family-blue",
    },
];

export function ProductFamily() {
    const { t } = useTranslation();
    return (
        <section
            className="relative py-32 px-6 bg-white overflow-hidden"
            data-testid="product-family-section"
            id="product-family"
        >
            <FmnWatermark position="bottom-left" size={700} opacity={0.05} rotate={14} />
            <div className="max-w-7xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-16">
                    <div className="flex justify-center mb-6">
                        <ForgetMeNotIcon size={48} />
                    </div>
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">
                        {t("product_family.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight">
                        {t("product_family.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95]">{t("product_family.headline_emphasis")}</em>
                    </h2>
                    <p className="mt-7 text-lg text-[#5C5A59] leading-relaxed font-light">
                        {t("product_family.lead")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10 items-end max-w-6xl mx-auto">
                    {EDITIONS.map((ed, i) => (
                        <motion.div
                            key={ed.testid}
                            {...FADE}
                            transition={{ ...FADE.transition, delay: 0.1 + i * 0.12 }}
                            className={`relative ${ed.scale}`}
                            data-testid={ed.testid}
                        >
                            <div className={`absolute inset-0 bg-gradient-to-b ${ed.glow} via-transparent to-transparent rounded-[2rem] pointer-events-none -z-10 opacity-60`} />
                            <div className={`relative ${ed.ratio} rounded-[2rem] overflow-hidden border ${ed.ring} bg-white shadow-rem-card`}>
                                <img
                                    src={ed.img}
                                    alt={t(`product_family.${ed.captionKey}`)}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <p className="mt-5 text-center text-sm font-serif italic text-[#5C5A59]">
                                {t(`product_family.${ed.captionKey}`)}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <motion.p
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.55 }}
                    className="mt-16 text-center text-base text-[#8A8786] font-serif italic max-w-2xl mx-auto"
                    data-testid="product-family-footnote"
                >
                    {t("product_family.footnote")}
                </motion.p>
            </div>
        </section>
    );
}
